import React, { useState, useRef } from "react";
import {
  FileText, Users, Briefcase, Plane, ChevronRight, ChevronLeft, ArrowLeft,
  CheckCircle2, Star, Sparkles, Send, RotateCcw,
} from "lucide-react";
import { Button } from "./Button";
import { landingContent } from "./i18n";

// Formspree endpoint – replace with your own after creating a free form at formspree.io
const FORM_ENDPOINT = "https://formspree.io/f/xdenkzyq";

// Ablefy checkout links (same as on the landing page)
const CHECKOUT = {
  rechnung: "https://myablefy.com/s/femmesmarketingstudio/angebots-rechnungs-studio-starter-sofort-starten-4d1e7e72/payment",
  crm: "https://myablefy.com/s/femmesmarketingstudio/crm-studio-starter-sofort-starten-und-kunden-verwalten-c0b0c7b7/payment",
  workforce: "https://myablefy.com/s/femmesmarketingstudio/kopie-von-angebots-rechnungs-studio-starter-0075396a/payment",
  travel: "https://myablefy.com/s/femmesmarketingstudio/business-travel-studio-starter-geschaeftsreisen-einfach-verwalten-und-abrechnen-faf5b5d3/payment",
};

const MODULE_ICONS = { rechnung: FileText, crm: Users, workforce: Briefcase, travel: Plane };

// Mapping: step2 option index -> module key
const FUNC_TO_MODULE = [
  "rechnung", // 0: Angebote erstellen
  "rechnung", // 1: Rechnungen schreiben
  "crm",      // 2: Kunden verwalten
  "crm",      // 3: Leads & Vertrieb
  "workforce", // 4: Projekte & Aufgaben
  "workforce", // 5: Mitarbeiter & Einsätze
  "workforce", // 6: Zeiterfassung
  "travel",   // 7: Geschäftsreisen
];

// Branche -> bonus points (additional interest)
const BRANCH_BONUS = {
  0: ["rechnung", "crm", "workforce", "travel"], // Agentur
  1: ["rechnung", "workforce"],                   // Handwerk
  2: ["workforce", "rechnung"],                   // Reinigung
  3: ["rechnung", "crm"],                         // IT
  4: ["rechnung", "crm"],                         // Handel
  5: ["rechnung", "crm", "workforce", "travel"], // Sonstiges
};

const TOTAL_STEPS = 6;

const BusinessCheckPage = ({ onBack, onWhiteLabel, lang = "de" }) => {
  const t = landingContent[lang]?.businessCheck;
  const [step, setStep] = useState(1);
  const [showResult, setShowResult] = useState(false);

  // Answers
  const [branch, setBranch] = useState(null);         // step1: index
  const [functions, setFunctions] = useState([]);      // step2: array of indices
  const [programCount, setProgramCount] = useState(null); // step3: index
  const [monthlyCost, setMonthlyCost] = useState("");  // step4: string
  const [wish, setWish] = useState("");                // step5: string
  const [contact, setContact] = useState({ name: "", company: "", email: "" }); // step6

  const [sendStatus, setSendStatus] = useState("idle"); // idle | sending | sent | error
  const topRef = useRef(null);

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Navigation
  const canNext = () => {
    if (step === 1) return branch !== null;
    if (step === 2) return functions.length > 0;
    if (step === 3) return programCount !== null;
    return true; // steps 4-6 are optional
  };

  const next = () => { if (step < TOTAL_STEPS) { setStep(step + 1); scrollTop(); } };
  const prev = () => { if (step > 1) { setStep(step - 1); scrollTop(); } };
  const finish = () => { setShowResult(true); scrollTop(); };

  // Toggle function selection (step 2)
  const toggleFunc = (idx) => {
    setFunctions((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  // ===== Zuordnungslogik =====
  const computeResults = () => {
    const scores = { rechnung: 0, crm: 0, workforce: 0, travel: 0 };

    // Direct points from selected functions
    functions.forEach((idx) => {
      const mod = FUNC_TO_MODULE[idx];
      if (mod) scores[mod] += 2; // strong match = 2 points per function
    });

    // Bonus from branch (1 point each = "additionally interesting")
    const bonuses = BRANCH_BONUS[branch] || [];
    bonuses.forEach((mod) => {
      scores[mod] += 1;
    });

    // Categorize: >=2 = strong match, 1 = additional interest, 0 = not shown
    const strong = [];
    const additional = [];
    Object.entries(scores).forEach(([key, score]) => {
      if (score >= 2) strong.push(key);
      else if (score === 1) additional.push(key);
    });

    return { strong, additional, scores };
  };

  // Cost calculation
  const computeSuiteCost = (modules) => {
    const mods = t?.modules || {};
    let total = 0;
    modules.forEach((key) => {
      if (mods[key]) total += mods[key].starterPrice;
    });
    return total;
  };

  // Submit form
  const submitForm = async () => {
    if (!contact.email.includes("@")) { setSendStatus("error"); return; }
    setSendStatus("sending");
    const { strong, additional } = computeResults();
    const formData = new FormData();
    formData.append("name", contact.name || "Nicht angegeben");
    formData.append("company", contact.company || "Nicht angegeben");
    formData.append("email", contact.email);
    formData.append("_replyto", contact.email);
    formData.append("branch", t?.step1?.options?.[branch] || "");
    formData.append("selectedFunctions", functions.map((i) => t?.step2?.options?.[i] || "").join(", "));
    formData.append("programCount", t?.step3?.options?.[programCount] || "");
    formData.append("monthlyCost", monthlyCost || "nicht angegeben");
    formData.append("wish", wish || "keine");
    formData.append("strongMatch", strong.map((k) => t?.modules?.[k]?.name || k).join(", "));
    formData.append("additionalMatch", additional.map((k) => t?.modules?.[k]?.name || k).join(", "));
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        setSendStatus("sent");
      } else {
        setSendStatus("error");
      }
    } catch {
      setSendStatus("error");
    }
  };

  const restart = () => {
    setStep(1); setShowResult(false); setBranch(null); setFunctions([]);
    setProgramCount(null); setMonthlyCost(""); setWish("");
    setContact({ name: "", company: "", email: "" }); setSendStatus("idle");
    scrollTop();
  };

  if (!t) return null;

  // ===== RESULT PAGE =====
  if (showResult) {
    const { strong, additional } = computeResults();
    const allRecommended = [...strong, ...additional];
    const userCost = parseFloat(monthlyCost) || 0;
    const suiteCost = computeSuiteCost(allRecommended);
    const programLabels = t.step3?.options || [];
    const programText = programCount !== null ? programLabels[programCount] : "";

    return (
      <div ref={topRef} className="min-h-screen bg-[var(--brand-bg)]">
        {/* Nav */}
        <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-[var(--brand-border)]">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
            <span className="font-semibold text-[var(--brand-ink)]">{t.navBrand}</span>
            <button onClick={onBack} className="text-sm text-[var(--brand-muted)] hover:text-[var(--brand-ink)] flex items-center gap-1">
              <ArrowLeft size={14} /> {t.backBtn}
            </button>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 py-12">
          {/* Heading */}
          <h1 className="text-3xl md:text-4xl font-bold text-[var(--brand-ink)] mb-2">{t.result.heading}</h1>
          <p className="text-[var(--brand-muted)] mb-10">{t.result.sub}</p>

          {/* Strong matches */}
          {strong.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Star size={18} className="text-yellow-500" />
                <h2 className="text-lg font-semibold text-[var(--brand-ink)]">{t.result.strongMatch}</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {strong.map((key) => {
                  const mod = t.modules[key];
                  const Icon = MODULE_ICONS[key];
                  return (
                    <div key={key} className="bg-white rounded-lg border-2 border-[var(--brand-accent)] p-6">
                      <div className="flex items-center gap-3 mb-3">
                        {Icon && <Icon size={24} className="text-[var(--brand-accent)]" />}
                        <h3 className="font-semibold text-[var(--brand-ink)]">{mod.name}</h3>
                      </div>
                      <p className="text-sm text-[var(--brand-muted)] mb-4">{mod.desc}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[var(--brand-muted)]">{t.result.fromPrice} <strong className="text-[var(--brand-ink)]">{mod.starterPrice} EUR</strong> {t.result.perMonth}</span>
                        <a href={CHECKOUT[key]} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-4 py-2 bg-[var(--brand-accent)] text-white text-sm font-medium rounded-md hover:opacity-90">
                          {t.result.buyBtn} <ChevronRight size={14} />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Additional matches */}
          {additional.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={18} className="text-[var(--brand-muted)]" />
                <h2 className="text-lg font-semibold text-[var(--brand-ink)]">{t.result.additionalMatch}</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {additional.map((key) => {
                  const mod = t.modules[key];
                  const Icon = MODULE_ICONS[key];
                  return (
                    <div key={key} className="bg-white rounded-lg border border-[var(--brand-border)] p-6">
                      <div className="flex items-center gap-3 mb-3">
                        {Icon && <Icon size={24} className="text-[var(--brand-muted)]" />}
                        <h3 className="font-semibold text-[var(--brand-ink)]">{mod.name}</h3>
                      </div>
                      <p className="text-sm text-[var(--brand-muted)] mb-4">{mod.desc}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[var(--brand-muted)]">{t.result.fromPrice} <strong className="text-[var(--brand-ink)]">{mod.starterPrice} EUR</strong> {t.result.perMonth}</span>
                        <a href={CHECKOUT[key]} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-2 border border-[var(--brand-accent)] text-[var(--brand-accent)] text-sm font-medium rounded-md hover:bg-[var(--brand-accent)] hover:text-white">
                          {t.result.buyBtn} <ChevronRight size={14} />
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Combo hint (2+ modules) */}
          {allRecommended.length >= 2 && (
            <div className="bg-[var(--brand-ink)] text-white rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-lg mb-2">{t.result.comboHeading}</h3>
              <p className="text-sm opacity-90">{t.result.comboText}</p>
            </div>
          )}

          {/* Cost comparison */}
          {userCost > 0 && (
            <div className="bg-white rounded-lg border border-[var(--brand-border)] p-6 mb-8">
              <h3 className="font-semibold text-lg text-[var(--brand-ink)] mb-4">{t.result.costHeading}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--brand-border)]">
                      <th className="text-left py-2 pr-4"></th>
                      <th className="text-left py-2 px-4 font-medium text-[var(--brand-muted)]">{t.result.costCurrent}</th>
                      <th className="text-left py-2 px-4 font-medium text-[var(--brand-accent)]">{t.result.costSuite}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-[var(--brand-border)]">
                      <td className="py-3 pr-4 font-medium text-[var(--brand-ink)]">{t.result.costPrograms}</td>
                      <td className="py-3 px-4 text-[var(--brand-muted)]">{programText}</td>
                      <td className="py-3 px-4 text-[var(--brand-ink)] font-medium">{t.result.costPlatform}</td>
                    </tr>
                    <tr className="border-b border-[var(--brand-border)]">
                      <td className="py-3 pr-4 font-medium text-[var(--brand-ink)]">{t.result.costMonthly}</td>
                      <td className="py-3 px-4 text-[var(--brand-muted)]">{userCost} EUR</td>
                      <td className="py-3 px-4 text-[var(--brand-ink)] font-medium">{suiteCost} EUR</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-medium text-[var(--brand-ink)]">{t.result.costYearly}</td>
                      <td className="py-3 px-4 text-[var(--brand-muted)]">{userCost * 12} EUR</td>
                      <td className="py-3 px-4 text-[var(--brand-ink)] font-medium">{suiteCost * 12} EUR</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* White-Label hint */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="font-semibold text-lg text-[var(--brand-ink)] mb-2">{t.result.wishHeading}</h3>
            <p className="text-sm text-[var(--brand-muted)] mb-2">{t.result.wishText}</p>
            <p className="text-xs text-[var(--brand-muted)] italic">{t.result.wishNote}</p>
          </div>

          {/* Contact form for sending results - IMMER anzeigen */}
          {sendStatus !== "sent" && (
            <div className="bg-white rounded-lg border border-[var(--brand-border)] p-6 mb-8">
              <h3 className="font-semibold text-[var(--brand-ink)] mb-4">{t.result.sendHeading}</h3>
              {!contact.email && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[var(--brand-ink)] mb-1">E-Mail-Adresse</label>
                  <input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    placeholder="deine@email.de"
                    className="w-full max-w-sm rounded-md border border-[var(--brand-border)] px-4 py-2 text-sm" />
                </div>
              )}
              {contact.email && (
                <p className="text-sm text-[var(--brand-muted)] mb-4">Wird gesendet an: <strong>{contact.email}</strong></p>
              )}
              <button onClick={submitForm} disabled={sendStatus === "sending" || !contact.email}
                style={{ backgroundColor: contact.email ? '#1a1a2e' : '#9ca3af', color: '#ffffff', padding: '14px 24px', fontSize: '16px', fontWeight: 'bold', borderRadius: '8px', border: 'none', cursor: contact.email ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Send size={16} /> {sendStatus === "sending" ? "Wird gesendet..." : t.result.sendBtn}
              </button>
              {sendStatus === "error" && <p className="text-red-500 text-sm mt-2">{t.result.sendError}</p>}
            </div>
          )}
          {sendStatus === "sent" && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8 flex items-center gap-2">
              <CheckCircle2 size={18} className="text-green-600" />
              <p className="text-sm text-green-700">{t.result.sendSuccess}</p>
            </div>
          )}

          {/* CTAs */}
          <div className="flex flex-wrap gap-3">
            <button onClick={onBack}
              className="px-6 py-3 bg-[var(--brand-accent)] text-white font-medium rounded-md hover:opacity-90">
              {t.result.ctaDemo}
            </button>
            <button onClick={onWhiteLabel}
              className="px-6 py-3 border border-[var(--brand-accent)] text-[var(--brand-accent)] font-medium rounded-md hover:bg-[var(--brand-accent)] hover:text-white">
              {t.result.ctaWhiteLabel}
            </button>
            <button onClick={restart}
              className="px-6 py-3 border border-[var(--brand-border)] text-[var(--brand-muted)] font-medium rounded-md hover:border-[var(--brand-ink)] hover:text-[var(--brand-ink)] flex items-center gap-2">
              <RotateCcw size={14} /> {t.result.ctaRestart}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== STEPPER =====
  const progressPct = (step / TOTAL_STEPS) * 100;

  return (
    <div ref={topRef} className="min-h-screen bg-[var(--brand-bg)]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-[var(--brand-border)]">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <span className="font-semibold text-[var(--brand-ink)]">{t.navBrand}</span>
          <button onClick={onBack} className="text-sm text-[var(--brand-muted)] hover:text-[var(--brand-ink)] flex items-center gap-1">
            <ArrowLeft size={14} /> {t.backBtn}
          </button>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-bold text-[var(--brand-ink)] mb-2">{t.mainTitle}</h1>
        <p className="text-[var(--brand-muted)] mb-8">{t.mainSub}</p>

        {/* Progress */}
        <div className="mb-2 flex items-center justify-between text-sm text-[var(--brand-muted)]">
          <span>{t.stepLabel} {step} {t.ofLabel} {TOTAL_STEPS}</span>
          <span>{Math.round(progressPct)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-8">
          <div className="bg-[var(--brand-accent)] h-2 rounded-full transition-all duration-300" style={{ width: `${progressPct}%` }} />
        </div>

        {/* Step 1: Branche – auto-advance on click */}
        {step === 1 && (
          <div>
            <h2 className="text-xl font-semibold text-[var(--brand-ink)] mb-6">{t.step1.question}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {t.step1.options.map((opt, i) => (
                <button key={i} onClick={() => { setBranch(i); setTimeout(() => { setStep(2); scrollTop(); }, 250); }}
                  className={`text-left p-4 rounded-lg border-2 transition-all ${branch === i ? "border-[var(--brand-accent)] bg-[var(--brand-accent)]/5" : "border-[var(--brand-border)] hover:border-[var(--brand-accent)]/50"}`}>
                  <span className="text-sm font-medium text-[var(--brand-ink)]">{opt}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Funktionen */}
        {step === 2 && (
          <div>
            <h2 className="text-xl font-semibold text-[var(--brand-ink)] mb-2">{t.step2.question}</h2>
            <p className="text-sm text-[var(--brand-muted)] mb-4">{t.step2.hint}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {t.step2.options.map((opt, i) => (
                <button key={i} onClick={() => toggleFunc(i)}
                  className={`text-left p-4 rounded-lg border-2 transition-all flex items-center gap-3 ${functions.includes(i) ? "border-[var(--brand-accent)] bg-[var(--brand-accent)]/5" : "border-[var(--brand-border)] hover:border-[var(--brand-accent)]/50"}`}>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${functions.includes(i) ? "border-[var(--brand-accent)] bg-[var(--brand-accent)]" : "border-gray-300"}`}>
                    {functions.includes(i) && <CheckCircle2 size={12} className="text-white" />}
                  </div>
                  <span className="text-sm font-medium text-[var(--brand-ink)]">{opt}</span>
                </button>
              ))}
            </div>
            {/* Großer Weiter-Button direkt unter den Kacheln - IMMER sichtbar */}
            <button onClick={() => { if (functions.length > 0) { setStep(3); scrollTop(); } }}
              style={{ backgroundColor: functions.length > 0 ? '#1a1a2e' : '#9ca3af', color: '#ffffff', padding: '20px', fontSize: '18px', fontWeight: 'bold', borderRadius: '12px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', border: 'none', cursor: functions.length > 0 ? 'pointer' : 'not-allowed' }}>
              {functions.length > 0 ? t.nextBtn : 'Bitte mindestens eine Aufgabe auswählen'} {functions.length > 0 && <ChevronRight size={22} />}
            </button>
          </div>
        )}

        {/* Step 3: Anzahl Programme – auto-advance on click */}
        {step === 3 && (
          <div>
            <h2 className="text-xl font-semibold text-[var(--brand-ink)] mb-6">{t.step3.question}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {t.step3.options.map((opt, i) => (
                <button key={i} onClick={() => { setProgramCount(i); setTimeout(() => { setStep(4); scrollTop(); }, 250); }}
                  className={`text-left p-4 rounded-lg border-2 transition-all ${programCount === i ? "border-[var(--brand-accent)] bg-[var(--brand-accent)]/5" : "border-[var(--brand-border)] hover:border-[var(--brand-accent)]/50"}`}>
                  <span className="text-sm font-medium text-[var(--brand-ink)]">{opt}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Kosten (optional) */}
        {step === 4 && (
          <div>
            <h2 className="text-xl font-semibold text-[var(--brand-ink)] mb-6">{t.step4.question}</h2>
            <div className="flex items-center gap-3 mb-6">
              <input type="number" min="0" value={monthlyCost} onChange={(e) => setMonthlyCost(e.target.value)}
                placeholder={t.step4.placeholder}
                className="w-full max-w-xs rounded-md border border-[var(--brand-border)] px-4 py-3 text-sm" />
              <span className="text-sm text-[var(--brand-muted)]">{t.step4.unit}</span>
            </div>
            <button onClick={() => { setStep(5); scrollTop(); }}
              style={{ backgroundColor: '#1a1a2e', color: '#ffffff', padding: '20px', fontSize: '18px', fontWeight: 'bold', borderRadius: '12px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', border: 'none', cursor: 'pointer' }}>
              {t.nextBtn} <ChevronRight size={20} />
            </button>
          </div>
        )}

        {/* Step 5: Wunschfunktion (optional) */}
        {step === 5 && (
          <div>
            <h2 className="text-xl font-semibold text-[var(--brand-ink)] mb-6">{t.step5.question}</h2>
            <textarea value={wish} onChange={(e) => setWish(e.target.value)}
              placeholder={t.step5.placeholder} maxLength={500} rows={4}
              className="w-full rounded-md border border-[var(--brand-border)] px-4 py-3 text-sm" />
            <p className="text-xs text-[var(--brand-muted)] mt-1 mb-6">{wish.length}/500</p>
            <button onClick={() => { setStep(6); scrollTop(); }}
              style={{ backgroundColor: '#1a1a2e', color: '#ffffff', padding: '20px', fontSize: '18px', fontWeight: 'bold', borderRadius: '12px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', border: 'none', cursor: 'pointer' }}>
              {t.nextBtn} <ChevronRight size={20} />
            </button>
          </div>
        )}

        {/* Step 6: Kontaktdaten (optional) */}
        {step === 6 && (
          <div>
            <h2 className="text-xl font-semibold text-[var(--brand-ink)] mb-2">{t.step6.question}</h2>
            <p className="text-sm text-[var(--brand-muted)] mb-6">{t.step6.hint}</p>
            <div className="space-y-4 max-w-md mb-6">
              <div>
                <label className="block text-sm font-medium text-[var(--brand-ink)] mb-1">{t.step6.nameLabel}</label>
                <input type="text" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })}
                  placeholder={t.step6.namePlaceholder}
                  className="w-full rounded-md border border-[var(--brand-border)] px-4 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--brand-ink)] mb-1">{t.step6.companyLabel}</label>
                <input type="text" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })}
                  placeholder={t.step6.companyPlaceholder}
                  className="w-full rounded-md border border-[var(--brand-border)] px-4 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--brand-ink)] mb-1">{t.step6.emailLabel}</label>
                <input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  placeholder={t.step6.emailPlaceholder}
                  className="w-full rounded-md border border-[var(--brand-border)] px-4 py-2 text-sm" />
              </div>
            </div>
            <button onClick={finish}
              style={{ backgroundColor: '#1a1a2e', color: '#ffffff', padding: '20px', fontSize: '18px', fontWeight: 'bold', borderRadius: '12px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', border: 'none', cursor: 'pointer' }}>
              {t.showResult} <ChevronRight size={20} />
            </button>
          </div>
        )}

        {/* Navigation buttons – sticky at bottom for mobile */}
        <div className="flex items-center justify-between mt-10 sticky bottom-0 bg-[var(--brand-bg)] py-4 border-t border-[var(--brand-border)]">
          <div>
            {step > 1 && (
              <button onClick={prev}
                className="flex items-center gap-1 px-4 py-2 text-sm text-[var(--brand-muted)] hover:text-[var(--brand-ink)]">
                <ChevronLeft size={16} /> {t.prevBtn}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {(step === 4 || step === 5 || step === 6) && step < TOTAL_STEPS && (
              <button onClick={next}
                className="px-4 py-2 text-sm text-[var(--brand-muted)] hover:text-[var(--brand-ink)]">
                {t.skipBtn}
              </button>
            )}
            {step === 6 && !contact.email && (
              <button onClick={finish}
                className="px-4 py-2 text-sm text-[var(--brand-muted)] hover:text-[var(--brand-ink)] underline">
                {t.step6.skipLabel}
              </button>
            )}
            {step < TOTAL_STEPS && (
              <button onClick={next} disabled={!canNext()}
                className="flex items-center gap-1 px-6 py-2 bg-[var(--brand-accent)] text-white text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-40">
                {t.nextBtn} <ChevronRight size={16} />
              </button>
            )}
            {step === TOTAL_STEPS && (
              <button onClick={finish}
                className="flex items-center gap-1 px-6 py-2 bg-[var(--brand-accent)] text-white text-sm font-medium rounded-md hover:opacity-90">
                {t.showResult} <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessCheckPage;
